/* Cart Upsell for Shopify - Standalone implementation
 * Optimized to prevent duplicate API calls
 */
(function () {
  'use strict';

  // Cart Upsell component definition
  window.theme = window.theme || {};

  theme.CartUpsell = (function () {
    var selectors = {
      upsellContainer: '.cart-upsell-container',
      upsellSlider: '.cart-upsell-slider',
      upsellItems: '.cart-upsell-item',
      prevButton: '.cart-upsell-prev',
      nextButton: '.cart-upsell-next',
      loadingIndicator: '.cart-upsell-loading',
      addToCartBtn: '.cart-upsell-add-btn',
      cartDrawer: '#CartDrawer',
      counterElement: '.cart-upsell-counter',
    };

    var classes = {
      active: 'is-active',
      loading: 'is-loading',
      btnLoading: 'btn--loading',
      hidden: 'hidden',
      initialized: 'initialized',
    };

    // Singleton instance
    var instance = null;
    var fetchInProgress = false;
    var lastProductId = null;
    var lastRequestTime = 0;
    var minimumRequestInterval = 2000; // 2 seconds minimum between requests
    var updateTimeout = null;
    var lastCartItemCount = 0;

    function CartUpsell(container) {
      // Only create one instance
      if (instance) {
        return instance;
      }

      this.container = container;
      this.upsellSlider = container.querySelector(selectors.upsellSlider);
      this.prevButton = container.querySelector(selectors.prevButton);
      this.nextButton = container.querySelector(selectors.nextButton);
      this.loadingIndicator = container.querySelector(
        selectors.loadingIndicator
      );

      this.currentIndex = 0;
      this.items = [];
      this.updateTimer = null;
      this.cachedRecommendations = {};

      instance = this;

      if (!this.upsellSlider) return instance;

      this.init();
      return instance;
    }

    CartUpsell.prototype = {
      init: function () {
        // Mark as initialized to prevent duplicate initialization
        if (this.container.classList.contains(classes.initialized)) {
          return;
        }
        this.container.classList.add(classes.initialized);

        // Set up event listeners for navigation
        if (this.prevButton) {
          this.prevButton.addEventListener('click', this.goToPrev.bind(this));
        }

        if (this.nextButton) {
          this.nextButton.addEventListener('click', this.goToNext.bind(this));
        }

        // Listen for cart events with throttling
        this.setupEventListeners();

        // Initial load of recommendations
        this.scheduleUpdate(0); // Immediate first load

        // Delegate listener for add to cart buttons
        this.container.addEventListener(
          'click',
          this.handleAddToCartClick.bind(this)
        );

        if (window.performance && window.performance.mark) {
          window.performance.mark('upsell-initialized');
        }
      },

      setupEventListeners: function () {
        // Use one function to handle all events
        var cartChangeHandler = this.handleCartChange.bind(this);

        // Standard cart events
        document.addEventListener('cart:updated', cartChangeHandler);
        document.addEventListener('cart:build', cartChangeHandler);
        document.addEventListener('ajaxProduct:added', cartChangeHandler);
        document.addEventListener('drawerOpen.CartDrawer', cartChangeHandler);
      },

      handleCartChange: function (event) {
        // Debounce updates
        this.scheduleUpdate(300);
      },

      scheduleUpdate: function (delay) {
        // Clear any existing timeout
        if (this.updateTimer) {
          clearTimeout(this.updateTimer);
        }

        // Set a new timeout
        this.updateTimer = setTimeout(() => {
          this.updateRecommendations();
        }, delay);
      },

      handleAddToCartClick: function (evt) {
        if (evt.target.closest(selectors.addToCartBtn)) {
          evt.preventDefault();
          var button = evt.target.closest(selectors.addToCartBtn);
          var variantId = button.dataset.variantId;
          this.addToCart(button, variantId);
        }
      },

      shouldUpdateRecommendations: function (cart) {
        // Avoid unnecessary API calls by checking:
        // 1. If we have a cart with items
        if (!cart || !cart.items || cart.items.length === 0) {
          this.hideUpsellContainer();
          return false;
        }

        // 2. If the cart has changed (by checking item count)
        var currentItemCount = cart.items.length;
        if (currentItemCount === lastCartItemCount) {
          return false;
        }
        lastCartItemCount = currentItemCount;

        // 3. If the first product ID has changed
        var firstProductId = cart.items[0].product_id;
        if (lastProductId === firstProductId) {
          // Same product, check if we already have recommendations
          if (this.cachedRecommendations[firstProductId]) {
            // Use cached results
            this.renderCachedRecommendations(firstProductId, cart);
            return false;
          }
        }
        lastProductId = firstProductId;

        // 4. Rate limiting - don't make requests too frequently
        var now = Date.now();
        if (now - lastRequestTime < minimumRequestInterval) {
          // Too soon, schedule an update for later
          this.scheduleUpdate(minimumRequestInterval - (now - lastRequestTime));
          return false;
        }

        return true;
      },

      updateRecommendations: function () {
        // Prevent multiple simultaneous requests
        if (fetchInProgress) {
          return;
        }

        var self = this;

        // Clear the timer
        if (this.updateTimer) {
          clearTimeout(this.updateTimer);
          this.updateTimer = null;
        }

        // Get cart data
        this.getCart()
          .then(function (cart) {
            // Check if cart is empty
            if (!cart || !cart.items || cart.items.length === 0) {
              self.hideUpsellContainer();
              return;
            }

            // Check if using manual product selection
            const upsellType = window.theme?.settings?.cartUpsellType || 'ai';

            if (upsellType === 'manual') {
              // Use manually selected products
              self.handleManualProducts(cart);
            } else {
              // Use AI recommendations (existing logic)
              // Check if we really need to update
              if (!self.shouldUpdateRecommendations(cart)) {
                return;
              }

              // Show loading state
              self.setLoadingState(true);
              fetchInProgress = true;
              lastRequestTime = Date.now();

              if (window.performance && window.performance.mark) {
                window.performance.mark('upsell-fetch-start');
              }

              // Call Shopify recommendations API
              self
                .fetchRecommendations(lastProductId)
                .then(function (recommendations) {
                  if (window.performance && window.performance.mark) {
                    window.performance.mark('upsell-fetch-complete');
                  }

                  // Cache the recommendations for this product ID
                  self.cachedRecommendations[lastProductId] = recommendations;

                  // Process and render the recommendations
                  self.processRecommendations(recommendations, cart);

                  fetchInProgress = false;
                })
                .catch(function (error) {
                  console.error('Error fetching recommendations:', error);
                  self.hideUpsellContainer();
                  fetchInProgress = false;
                });
            }
          })
          .catch(function (error) {
            console.error('Error getting cart:', error);
            self.hideUpsellContainer();
          });
      },

      handleManualProducts: function (cart) {
        // Get manually selected products from settings
        const manualProducts = window.theme?.settings?.cartUpsellProducts || [];

        if (manualProducts.length === 0) {
          this.hideUpsellContainer();
          return;
        }

        // Filter out products that are already in the cart
        const cartProductIds = cart.items.map((item) => item.product_id);

        const filteredProducts = manualProducts.filter((product) => {
          // Skip products already in cart
          if (cartProductIds.includes(product.id)) {
            return false;
          }

          // Only include available products
          return (
            product.available && product.variants && product.variants.length > 0
          );
        });

        // Limit to the configured number
        const limit = window.theme?.settings?.cartUpsellLimit || 5;
        const limitedProducts = filteredProducts.slice(0, limit);

        if (limitedProducts.length === 0) {
          this.hideUpsellContainer();
          return;
        }

        // Show container and render products
        this.container.classList.remove('hidden');
        this.renderManualProducts(limitedProducts);
      },

      renderCachedRecommendations: function (productId, cart) {
        // Use cached recommendations to avoid API call
        var recommendations = this.cachedRecommendations[productId];
        this.processRecommendations(recommendations, cart);
      },

      processRecommendations: function (recommendations, cart) {
        // Filter out products that are already in the cart
        const cartProductIds = cart.items.map((item) => item.product_id);

        const filteredProducts = recommendations.filter((product) => {
          // Skip products already in cart
          if (cartProductIds.includes(product.id)) {
            return false;
          }

          // Skip products with "FREE GIFT" or "SAMPLING" in the title (case insensitive)
          const title = product.title.toUpperCase();
          const tags = product.tags || [];

          if (
            title.includes('FREE GIFT') ||
            title.includes('SAMPLING') ||
            tags.some((tag) => tag.toLowerCase() === 'free_gift')
          ) {
            return false;
          }

          return true;
        });

        // Limit to the configured number of products
        const limit = window.theme?.settings?.cartUpsellLimit || 5;
        const limitedProducts = filteredProducts.slice(0, limit);

        if (limitedProducts.length === 0) {
          this.hideUpsellContainer();
          return;
        }

        // Show container if we have recommendations
        this.container.classList.remove(classes.hidden);
        this.renderRecommendations(limitedProducts);
      },

      hideUpsellContainer: function () {
        this.setLoadingState(false);
        this.container.classList.add(classes.hidden);
      },

      getCart: function () {
        return fetch('/cart.js', {
          credentials: 'same-origin',
          method: 'GET',
        }).then(function (response) {
          return response.json();
        });
      },

      fetchRecommendations: function (productId) {
        // Use Shopify's recommendations API
        const limit = window.theme?.settings?.cartUpsellLimit || 5;

        // Request extra in case some are filtered
        const url = `/recommendations/products.json?product_id=${productId}&limit=${
          limit * 2
        }`;

        return fetch(url)
          .then(function (response) {
            return response.json();
          })
          .then(function (data) {
            return data.products || [];
          });
      },

      renderRecommendations: function (products) {
        const self = this;

        if (products.length === 0) {
          self.hideUpsellContainer();
          return;
        }

        // Add counter element if it doesn't exist
        if (!this.counterElement) {
          this.counterElement = document.createElement('div');
          this.counterElement.className = 'cart-upsell-counter';
          // Insert it between the navigation buttons or wherever appropriate
          this.prevButton.insertAdjacentElement(
            'beforebegin',
            this.counterElement
          );
        }

        // Get grid setting (1 or 2 products per slide)
        const gridSize = parseInt(
          window.theme?.settings?.cartUpsellGrid || '1'
        );

        // Group products into slides based on grid size
        const slides = this.groupProductsIntoSlides(products, gridSize);

        let html = '';

        slides.forEach(function (slideProducts, slideIndex) {
          const activeClass = slideIndex === 0 ? classes.active : '';

          html += `<div class="cart-upsell-item ${activeClass}" data-index="${slideIndex}">`;
          html += `<div class="cart-upsell-grid cart-upsell-grid-${gridSize}">`;

          slideProducts.forEach(function (product) {
            const formattedPrice = this.formatMoney(product.price);
            let comparePrice = '';

            // Add compare at price if available
            if (
              product.compare_at_price &&
              product.compare_at_price > product.price
            ) {
              comparePrice = `<span class="cart-upsell-compare-price">
                ${this.formatMoney(product.compare_at_price)}
              </span>`;
            }

            html += `
              <div class="cart-upsell-product">
                <div class="cart-upsell-product-inner">
                  <!-- Product image -->
                  <div class="cart-upsell-image">
                    <a href="${product.url}">
                      <img src="${this.getSizedImageUrl(
                        product.featured_image,
                        '200x'
                      )}" alt="${product.title}">
                    </a>
                  </div>

                  <!-- Product content wrapper -->
                  <div class="cart-upsell-content">
                    <!-- Product details -->
                    <div class="cart-upsell-details">
                      <h4 class="cart-upsell-title">
                        <a href="${product.url}">${product.title}</a>
                      </h4>

                      <!-- Price display with compare at price -->
                      <div class="cart-upsell-price-wrapper">
                        ${comparePrice}
                        <div class="cart-upsell-price${
                          comparePrice ? ' cart-upsell-price--sale' : ''
                        }">
                          ${formattedPrice}
                        </div>
                      </div>
                    </div>
                    <div class="cart-upsell-actions cua-inside">
                        <!-- Add to cart button -->
                        <button type="button" class="btn btn--small cart-upsell-add-btn"
                                data-variant-id="${product.variants[0].id}">
                          ${window.theme?.strings?.addToCart || 'Add to cart'}
                        </button>
                    </div>
                  </div>
                </div>
                <div class="cart-upsell-actions cua-outside">
                    <!-- Add to cart button -->
                    <button type="button" class="btn btn--small cart-upsell-add-btn"
                            data-variant-id="${product.variants[0].id}">
                      ${window.theme?.strings?.addToCart || 'Add to cart'}
                    </button>
                </div>
              </div>
            `;
          }, this);

          html += '</div></div>';
        }, this);

        self.upsellSlider.innerHTML = html;
        self.items = self.upsellSlider.querySelectorAll(selectors.upsellItems);
        self.currentIndex = 0;

        // Update navigation buttons state
        self.updateNavigationState();

        // Remove loading state
        self.setLoadingState(false);

        if (window.performance && window.performance.mark) {
          window.performance.mark('upsell-rendered');
        }
        // Update counter after rendering
        this.updateCounter();
      },

      renderManualProducts: function (products) {
        const self = this;

        if (products.length === 0) {
          self.hideUpsellContainer();
          return;
        }

        // Add counter element if it doesn't exist
        if (!this.counterElement) {
          this.counterElement = document.createElement('div');
          this.counterElement.className = 'cart-upsell-counter';
          // Insert it between the navigation buttons
          this.prevButton.insertAdjacentElement(
            'beforebegin',
            this.counterElement
          );
        }

        // Get grid setting (1 or 2 products per slide)
        const gridSize = parseInt(
          window.theme?.settings?.cartUpsellGrid || '1'
        );

        // Group products into slides based on grid size
        const slides = this.groupProductsIntoSlides(products, gridSize);

        let html = '';

        slides.forEach(function (slideProducts, slideIndex) {
          const activeClass = slideIndex === 0 ? classes.active : '';

          html += `<div class="cart-upsell-item ${activeClass}" data-index="${slideIndex}">`;
          html += `<div class="cart-upsell-grid cart-upsell-grid-${gridSize}">`;

          slideProducts.forEach(function (product, productIndex) {
            // Get the first available variant or first variant
            const selectedVariant =
              product.variants.find((v) => v.available) || product.variants[0];
            if (!selectedVariant) return;

            const formattedPrice = this.formatMoney(selectedVariant.price);
            let comparePrice = '';

            // Add compare at price if available
            if (
              selectedVariant.compare_at_price &&
              selectedVariant.compare_at_price > selectedVariant.price
            ) {
              comparePrice = `<span class="cart-upsell-compare-price">
              ${this.formatMoney(selectedVariant.compare_at_price)}
            </span>`;
            }

            // Build variant selector if multiple variants
            const uniqueIndex = slideIndex * gridSize + productIndex;
            let variantSelector = '';
            if (product.variants.length > 1) {
              variantSelector = `
              <select name="variant-${uniqueIndex}" class="cart-upsell-variant-select" data-product-index="${uniqueIndex}">
                ${product.variants
                  .map((variant) => {
                    if (!variant.available) return '';
                    return `<option value="${variant.id}"
                    data-price="${variant.price}"
                    data-compare-price="${
                      variant.compare_at_price || variant.price
                    }">
                    ${variant.title}
                  </option>`;
                  })
                  .join('')}
              </select>
            `;
            }

            html += `
            <div class="cart-upsell-product" data-product-id="${
              product.id
            }" data-unique-index="${uniqueIndex}">
              <div class="cart-upsell-product-inner">
                <!-- Product image -->
                <div class="cart-upsell-image">
                  <a href="${product.url}">
                    <img src="${
                      product.featured_image.startsWith('//')
                        ? 'https:' + product.featured_image
                        : product.featured_image
                    }" alt="${product.title}">
                  </a>
                </div>

                <!-- Product content wrapper -->
                <div class="cart-upsell-content">
                  <!-- Product details -->
                  <div class="cart-upsell-details">
                    <h4 class="cart-upsell-title">
                      <a href="${product.url}">${product.title}</a>
                    </h4>
                    <!-- Price display with compare at price -->
                    <div class="cart-upsell-price-wrapper" data-price-wrapper>
                      ${comparePrice}
                      <div class="cart-upsell-price${
                        comparePrice ? ' cart-upsell-price--sale' : ''
                      }" data-price>
                        ${formattedPrice}
                      </div>
                    </div>
                  </div>

                   <div class="cart-upsell-actions cua-inside">
                    ${
                      variantSelector
                        ? `<div class="cart-upsell-variants">${variantSelector}</div>`
                        : ''
                    }

                    <button type="button" class="btn btn--small cart-upsell-add-btn"
                            data-variant-id="${selectedVariant.id}">
                      ${window.theme?.strings?.addToCart || 'Add to cart'}
                    </button>
                  </div>
                </div>
              </div>

              <div class="cart-upsell-actions cua-outside">
                    ${
                      variantSelector
                        ? `<div class="cart-upsell-variants">${variantSelector}</div>`
                        : ''
                    }

                    <button type="button" class="btn btn--small cart-upsell-add-btn"
                            data-variant-id="${selectedVariant.id}">
                      ${window.theme?.strings?.addToCart || 'Add to cart'}
                    </button>
                  </div>
                </div>
          `;
          }, this);

          html += '</div></div>';
        }, this);

        self.upsellSlider.innerHTML = html;
        self.items = self.upsellSlider.querySelectorAll(selectors.upsellItems);
        self.currentIndex = 0;

        // Add event listeners for variant selectors
        const variantSelects = self.upsellSlider.querySelectorAll(
          '.cart-upsell-variant-select'
        );
        variantSelects.forEach((select) => {
          select.addEventListener(
            'change',
            self.handleVariantChange.bind(self)
          );
        });

        // Update navigation buttons state
        self.updateNavigationState();

        // Remove loading state
        self.setLoadingState(false);

        if (window.performance && window.performance.mark) {
          window.performance.mark('upsell-rendered');
        }

        // Update counter after rendering
        this.updateCounter();
      },

      groupProductsIntoSlides: function (products, gridSize) {
        const slides = [];

        for (let i = 0; i < products.length; i += gridSize) {
          slides.push(products.slice(i, i + gridSize));
        }

        return slides;
      },

      handleVariantChange: function (event) {
        const select = event.target;
        const selectedOption = select.options[select.selectedIndex];
        const variantId = selectedOption.value;
        const price = selectedOption.dataset.price;
        const comparePrice = selectedOption.dataset.comparePrice;

        // Find the parent product (could be in a grid)
        const product = select.closest('.cart-upsell-product');
        if (!product) return;

        // Update the add to cart button
        const addButton = product.querySelector('.cart-upsell-add-btn');
        if (addButton) {
          addButton.dataset.variantId = variantId;
        }

        // Update price display
        const priceWrapper = product.querySelector('[data-price-wrapper]');
        if (priceWrapper) {
          let priceHtml = '';

          if (comparePrice && comparePrice !== price && comparePrice > price) {
            priceHtml += `<span class="cart-upsell-compare-price">
              ${this.formatMoney(comparePrice)}
            </span>`;
          }

          priceHtml += `<div class="cart-upsell-price${
            comparePrice && comparePrice > price
              ? ' cart-upsell-price--sale'
              : ''
          }" data-price>
            ${this.formatMoney(price)}
          </div>`;

          priceWrapper.innerHTML = priceHtml;
        }
      },

      goToNext: function () {
        if (this.currentIndex >= this.items.length - 1) return;

        this.items[this.currentIndex].classList.remove(classes.active);
        this.currentIndex++;
        this.items[this.currentIndex].classList.add(classes.active);

        this.updateNavigationState();
        this.updateCounter();
      },

      goToPrev: function () {
        if (this.currentIndex <= 0) return;

        this.items[this.currentIndex].classList.remove(classes.active);
        this.currentIndex--;
        this.items[this.currentIndex].classList.add(classes.active);

        this.updateNavigationState();
        this.updateCounter();
      },

      updateCounter: function () {
        if (!this.counterElement) return;

        this.counterElement.textContent = `(${this.currentIndex + 1}/${
          this.items.length
        })`;
      },
      updateNavigationState: function () {
        if (!this.items.length) {
          this.prevButton.disabled = true;
          this.nextButton.disabled = true;
          return;
        }

        this.prevButton.disabled = this.currentIndex === 0;
        this.nextButton.disabled = this.currentIndex >= this.items.length - 1;
      },

      addToCart: function (button, variantId) {
        var self = this;

        button.classList.add(classes.btnLoading);

        // Add item to cart
        fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            id: variantId,
            quantity: 1,
          }),
        })
          .then(function (response) {
            return response.json();
          })
          .then(function (data) {
            // Update the cart drawer
            if (window.theme && window.theme.cart) {
              window.theme.cart.getCartProductMarkup().then(function () {
                document.dispatchEvent(new CustomEvent('cart:build'));
              });
            } else {
              // Fallback method if theme.cart is unavailable
              self.refreshCart();
            }

            button.classList.remove(classes.btnLoading);
          })
          .catch(function (error) {
            console.error('Error adding product to cart:', error);
            button.classList.remove(classes.btnLoading);
          });
      },

      refreshCart: function () {
        fetch('/cart?view=drawer')
          .then(function (response) {
            return response.text();
          })
          .then(function (html) {
            // Find and update the cart products container
            var parser = new DOMParser();
            var doc = parser.parseFromString(html, 'text/html');
            var productsContainer = document.querySelector('[data-products]');

            if (productsContainer) {
              var newProducts = doc.querySelector('[data-products]');
              if (newProducts) {
                productsContainer.innerHTML = newProducts.innerHTML;
              }
            }

            // Refresh upsell recommendations
            document.dispatchEvent(new CustomEvent('cart:updated'));
          });
      },

      setLoadingState: function (isLoading) {
        if (isLoading) {
          this.container.classList.add(classes.loading);
          if (this.loadingIndicator) {
            this.loadingIndicator.style.display = 'block';
          }
        } else {
          this.container.classList.remove(classes.loading);
          if (this.loadingIndicator) {
            this.loadingIndicator.style.display = 'none';
          }
        }
      },

      formatMoney: function (cents) {
        if (typeof cents === 'string') {
          cents = cents.replace('.', '');
        }

        var value = '';
        var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
        var formatString =
          (window.theme &&
            window.theme.settings &&
            window.theme.settings.moneyFormat) ||
          '${{amount}}';

        function formatWithDelimiters(number, precision, thousands, decimal) {
          precision = precision || 2;
          thousands = thousands || ',';
          decimal = decimal || '.';

          if (isNaN(number) || number == null) {
            return 0;
          }

          number = (number / 100.0).toFixed(precision);

          var parts = number.split('.');
          var dollarsAmount = parts[0].replace(
            /(\d)(?=(\d\d\d)+(?!\d))/g,
            '$1' + thousands
          );
          var centsAmount = parts[1] ? decimal + parts[1] : '';

          return dollarsAmount + centsAmount;
        }

        switch (formatString.match(placeholderRegex)[1]) {
          case 'amount':
            value = formatWithDelimiters(cents, 2);
            break;
          case 'amount_no_decimals':
            value = formatWithDelimiters(cents, 0);
            break;
          case 'amount_with_comma_separator':
            value = formatWithDelimiters(cents, 2, '.', ',');
            break;
          case 'amount_no_decimals_with_comma_separator':
            value = formatWithDelimiters(cents, 0, '.', ',');
            break;
          case 'amount_no_decimals_with_space_separator':
            value = formatWithDelimiters(cents, 0, ' ');
            break;
        }

        return formatString.replace(placeholderRegex, value);
      },

      getSizedImageUrl: function (src, size) {
        if (!src) {
          return null;
        }

        if (size === null) {
          return src;
        }

        if (size === 'master') {
          return src.replace(/http(s)?:/, '');
        }

        var match = src.match(
          /\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif)(\?v=\d+)?$/i
        );

        if (match) {
          var prefix = src.split(match[0]);
          var suffix = match[0];

          return (prefix[0] + '_' + size + suffix).replace(/http(s)?:/, '');
        } else {
          return null;
        }
      },
    };

    return CartUpsell;
  })();

  // Initialize cart upsell on document ready - using a single instance
  document.addEventListener('DOMContentLoaded', function () {
    initCartUpsell();
  });

  // Function to initialize cart upsell component
  function initCartUpsell() {
    var upsellContainer = document.querySelector(
      '.cart-upsell-container:not(.initialized)'
    );
    if (upsellContainer) {
      new theme.CartUpsell(upsellContainer);
    }
  }

  // Listen for drawer opening to initialize upsell if not already done
  document.addEventListener('drawerOpen.CartDrawer', function () {
    setTimeout(initCartUpsell, 100);
  });
})();
